import type { Identifier } from "ra-core";

import {
  compareIdeas,
  IDEA_STATUSES,
  isIdeaStatus,
  isPriority,
} from "./labels";
import type { IdeaStatus, MarketIdea, MarketPriority } from "./types";

export type IdeaColumns = Record<IdeaStatus, MarketIdea[]>;

/** Ideas per board column, each column sorted by priority then recency. */
export const groupIdeasByStatus = (
  ideas: readonly MarketIdea[],
): IdeaColumns => {
  const columns = Object.fromEntries(
    IDEA_STATUSES.map((status) => [status, [] as MarketIdea[]]),
  ) as IdeaColumns;
  for (const idea of ideas) {
    // Unknown statuses (hand-written SQL) land in "nueva" instead of vanishing.
    const status = isIdeaStatus(idea.status) ? idea.status : "nueva";
    columns[status] = [...columns[status], idea];
  }
  for (const status of IDEA_STATUSES) {
    columns[status] = [...columns[status]].sort(compareIdeas);
  }
  return columns;
};

export type IdeaField = "status" | "priority";

/**
 * The `useUpdate` payload of a quick status / priority change, or null when
 * nothing would change (same value, or a value the database would reject).
 */
export const ideaFieldPatch = <F extends IdeaField>(
  idea: MarketIdea,
  field: F,
  value: MarketIdea[F],
): {
  id: Identifier;
  data: Partial<MarketIdea>;
  previousData: MarketIdea;
} | null => {
  const valid = field === "status" ? isIdeaStatus(value) : isPriority(value);
  if (!valid || idea[field] === value) return null;
  return { id: idea.id, data: { [field]: value }, previousData: idea };
};

/** A copy of `ideas` with one idea moved to `status` (optimistic board state). */
export const withIdeaStatus = (
  ideas: readonly MarketIdea[],
  id: Identifier,
  status: IdeaStatus,
): MarketIdea[] =>
  ideas.map((idea) =>
    String(idea.id) === String(id) ? { ...idea, status } : idea,
  );

export interface IdeaFormValues {
  title: string;
  description: string;
  priority: MarketPriority;
  status: IdeaStatus;
  sector: string;
  product_type: string;
}

export type IdeaPayload = Pick<MarketIdea, "title" | "priority" | "status"> & {
  description: string | null;
  sector: string | null;
  product_type: string | null;
};

const MAX_TITLE = 200;

/** Trimmed payload for create/update, or the i18n key of the first error. */
export const normalizeIdeaForm = (
  values: IdeaFormValues,
): { ok: true; data: IdeaPayload } | { ok: false; error: string } => {
  const title = values.title.trim();
  if (!title) return { ok: false, error: "market.ideas.form.title_required" };
  if (title.length > MAX_TITLE)
    return { ok: false, error: "market.ideas.form.title_too_long" };
  const optional = (value: string) => value.trim() || null;
  return {
    ok: true,
    data: {
      title,
      description: optional(values.description),
      priority: isPriority(values.priority) ? values.priority : "media",
      status: isIdeaStatus(values.status) ? values.status : "nueva",
      sector: optional(values.sector),
      product_type: optional(values.product_type),
    },
  };
};

export const ideaFormDefaults = (
  idea?: Partial<MarketIdea>,
): IdeaFormValues => ({
  title: idea?.title ?? "",
  description: idea?.description ?? "",
  priority: idea?.priority ?? "media",
  status: idea?.status ?? "nueva",
  sector: idea?.sector ?? "",
  product_type: idea?.product_type ?? "",
});
